import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";

import { HeroSection } from "@/components/landing/HeroSection";
import { SocialProofBar } from "@/components/landing/SocialProofBar";
import { TimelineScroller } from "@/components/landing/DemoPreview";
import { PrivacyHardware } from "@/components/landing/PrivacyHardware";
import { MasterclassSection } from "@/components/landing/MasterclassSection";
import { StaffAssistant } from "@/components/landing/StaffAssistant";
import { FoundingBenefits } from "@/components/landing/BenefitsGrid";
import { FooterCTA } from "@/components/landing/FooterCTA";
import { ChatWidget } from "@/components/landing/ChatWidget";
import { FeaturesShowcase } from "@/components/landing/FeaturesShowcase";
import { SystemStatus } from "@/components/landing/SystemStatus";
import { RoadmapSection } from "@/components/landing/RoadmapSection";
import TalkToEcho from "@/components/landing/TalkToEcho";

import { AuthProvider, AdminOnlyRoute, GuestAllowedRoute } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import ThankYouPage from "@/pages/ThankYouPage";
import AdminLogin from "@/pages/AdminLogin";
// AdminDashboard removed — /admin/founding-50 now redirects to /admin/command-center
import SOPBuilder from "@/pages/SOPBuilder";
import AdminInventory from "@/pages/AdminInventory";
import VisionDashboard from "@/pages/VisionDashboard";
import ReceptionistPage from "@/pages/ReceptionistPage";
import StaffSyncDashboard from "@/pages/StaffSyncDashboard";
import SafetyLogPage from "@/pages/SafetyLogPage";
import RecoveryOutreach from "@/pages/RecoveryOutreach";
import MaintenancePage from "@/pages/MaintenancePage";
import AuthCallback from "@/pages/AuthCallback";

// Trial System Pages (Phase 2)
import TrialDashboard from "@/pages/TrialDashboard";
import TrialAuth from "@/pages/TrialAuth";
import TrialVision from "@/pages/TrialVision";
import TrialAnalytics from "@/pages/TrialAnalytics";
import TrialSopBuilder from "@/pages/TrialSopBuilder";
import TrialCheckout from "@/pages/TrialCheckout";

// Command Center
import CommandCenter from "@/pages/CommandCenter";

// Restaurant Onboarding
import RestaurantOnboarding from "@/pages/onboarding/RestaurantOnboarding";
import AIOnboarding from "@/pages/onboarding/AIOnboarding";

const LandingPage = ({ foundingMember = false }) => {
  const [remaining, setRemaining] = useState(undefined);
  const [fomoNotification, setFomoNotification] = useState(null);

  useEffect(() => {
    const fetchSpots = async () => {
      try {
        const res = await axios.get(`${API}/founding-spots`);
        setRemaining(res.data.remaining);
      } catch { setRemaining(27); }
    };
    fetchSpots();

    // Listen for real-time "new founder" events via WebSocket
    const wsUrl = `${API.replace('http', 'ws').replace('/api', '')}/ws/updates/default`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_founder') {
            setRemaining(data.remaining);
            setFomoNotification(`Founding Member #${data.member_number} just joined!`);
            setTimeout(() => setFomoNotification(null), 5000);
          }
        } catch {}
      };
    } catch {}
    return () => { if (ws) ws.close(); };
  }, []);

  return (
    <div className="min-h-screen bg-[#040F49] relative">
      <div className="noise-overlay" />
      <SocialProofBar />
      <HeroSection />

      {/* Real-time FOMO notification */}
      {fomoNotification && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[70] animate-in slide-in-from-top">
          <div className="px-6 py-3 rounded-full shadow-2xl" style={{
            background: 'linear-gradient(135deg, #D4AF37, #b8941f)',
            boxShadow: '0 8px 32px rgba(212,175,55,0.4)',
          }}>
            <span className="text-[#040F49] font-bold text-sm">{fomoNotification}</span>
          </div>
        </div>
      )}

      {/* System Status Bar */}
      <SystemStatus />

      <div className="section-divider" />
      <TimelineScroller />
      
      {/* Features Showcase - All 5 supervisors */}
      <div className="section-divider" />
      <FeaturesShowcase />

      <div className="section-divider" />
      <PrivacyHardware />

      <div className="section-divider" />
      <MasterclassSection />

      <div className="section-divider" />
      <StaffAssistant />

      {/* Roadmap & Pricing - Shows why Founders win */}
      <div className="section-divider" />
      <RoadmapSection remaining={remaining} />

      <div className="section-divider" />
      <FoundingBenefits remaining={remaining} />

      <div className="section-divider" />
      <FooterCTA foundingMember={foundingMember} />

      {/* AI Argus Chat - Quiz is now integrated inside the chat */}
      <ChatWidget
        agentType="argus"
        title="Talk to AI Argus"
        idleAutoOpen={true}
        idleTimeout={60000}
      />

      {/* Talk to Echo — Voice AI Widget (VAPI Web Call) */}
      <TalkToEcho />
    </div>
  );
};

function App() {
  const [maintenanceMode, setMaintenanceMode] = useState(null); // null = checking
  const [foundingMember, setFoundingMember] = useState(false);

  useEffect(() => {
    // Emergent preview and local dev always bypass maintenance — never block the build env
    const hostname = window.location.hostname;
    const isPreviewEnv =
      hostname.includes("emergentagent.com") ||
      hostname === "localhost" ||
      hostname === "127.0.0.1";

    if (isPreviewEnv) {
      setMaintenanceMode(false);
      return;
    }

    // CRITICAL: If returning from OAuth callback, skip maintenance check
    if (window.location.hash?.includes('session_id=')) {
      setMaintenanceMode(false);
      return;
    }

    // Check if user has an active session (bypass maintenance for authenticated users)
    const sessionToken = localStorage.getItem('argus_session_token');
    if (sessionToken) {
      // Verify session is still valid
      fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
        credentials: 'include',
      })
        .then(r => {
          if (r.ok) {
            // Authenticated founding member — bypass maintenance
            setMaintenanceMode(false);
            return;
          }
          // Session invalid — clear and check maintenance
          localStorage.removeItem('argus_session_token');
          localStorage.removeItem('argus_user');
          return checkMaintenance();
        })
        .catch(() => checkMaintenance());
      return;
    }

    checkMaintenance();

    function checkMaintenance() {
      axios
        .get(`${API}/maintenance-status?ts=${Date.now()}`)
        .then((r) => {
          setMaintenanceMode(r.data.maintenance);
          setFoundingMember(r.data.founding_member || false);
        })
        .catch(() => setMaintenanceMode(false));
    }
  }, []);

  // While checking, show a dark blank screen to prevent flash
  if (maintenanceMode === null) {
    return <div style={{ background: "#040F49", minHeight: "100vh" }} />;
  }

  // In maintenance — allow /admin/* paths and OAuth callback through
  const isAdminPath = window.location.pathname.startsWith("/admin");
  const isAuthCallback = window.location.hash?.includes('session_id=');
  if (maintenanceMode && !isAdminPath && !isAuthCallback) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<MaintenancePage />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <AppRouter foundingMember={foundingMember} />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

// CRITICAL: Detect session_id in hash synchronously during render (not in useEffect)
// This prevents race conditions with AuthProvider's checkAuth
function AppRouter({ foundingMember }) {
  // Check URL fragment for OAuth callback session_id
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <AuthProvider>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage foundingMember={foundingMember} />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          
          {/* Protected Admin Routes */}
          {/* Redirect bare /admin and legacy routes to Command Centre */}
          <Route path="/admin" element={<Navigate to="/admin/command-center" replace />} />
          <Route path="/admin/founding-50" element={<Navigate to="/admin/command-center" replace />} />
          <Route path="/admin/dashboard" element={<Navigate to="/admin/command-center" replace />} />
          <Route path="/admin/inventory" element={
            <AdminOnlyRoute><AdminInventory /></AdminOnlyRoute>
          } />
          <Route path="/admin/vision" element={
            <AdminOnlyRoute><VisionDashboard /></AdminOnlyRoute>
          } />
          <Route path="/admin/staff-sync" element={
            <AdminOnlyRoute><StaffSyncDashboard /></AdminOnlyRoute>
          } />
          <Route path="/admin/safety-log" element={
            <AdminOnlyRoute><SafetyLogPage /></AdminOnlyRoute>
          } />
          <Route path="/admin/recovery" element={
            <AdminOnlyRoute><RecoveryOutreach /></AdminOnlyRoute>
          } />
          <Route path="/admin/command-center" element={
            <AdminOnlyRoute><CommandCenter /></AdminOnlyRoute>
          } />
          
          {/* Public AI Receptionist */}
          <Route path="/receptionist" element={<ReceptionistPage />} />
          
          {/* Guest-Allowed Routes (SOP Builder Sandbox) */}
          <Route path="/dashboard/sop-builder" element={
            <GuestAllowedRoute><SOPBuilder /></GuestAllowedRoute>
          } />
          
          {/* Trial System Routes (Phase 2) */}
          <Route path="/trial/login" element={<TrialAuth />} />
          <Route path="/trial/register" element={<TrialAuth />} />
          <Route path="/trial/dashboard" element={<TrialDashboard />} />
          <Route path="/trial/vision" element={<TrialVision />} />
          <Route path="/trial/sop-builder" element={<TrialSopBuilder />} />
          <Route path="/trial/analytics" element={<TrialAnalytics />} />
          <Route path="/trial/checkout" element={<TrialCheckout />} />
          
          {/* Restaurant Onboarding */}
          <Route path="/onboarding" element={<AIOnboarding />} />
          <Route path="/onboarding/manual" element={<RestaurantOnboarding />} />
          <Route path="/trial/onboarding" element={<AIOnboarding />} />
        </Routes>
      </AuthProvider>
  );
}

export default App;

